/* ── MARKDOWN FORMATTER ──
   Moved out of App.jsx (2026-10-04) so it can be unit-tested (format.test.js).
   fmt() turns model answer text into the HTML that App.jsx renders with
   dangerouslySetInnerHTML. HTML escaping runs FIRST, before any markdown pass, so no
   model- or user-supplied markup (e.g. <script>) can ever reach the DOM unescaped. */

// Placeholder for an escaped pipe "\|" inside a table cell while the row is split on "|".
// A Unicode private-use character, so it cannot collide with real answer text.
const PIPE_PLACEHOLDER = "";

// Column alignment from a separator cell: ":---:" centre, "---:" right, ":---" left,
// "---" none (browser default).
function cellAlign(sepCell) {
  const c = sepCell.trim();
  const left = c.startsWith(":"), right = c.endsWith(":");
  if (left && right) return "center";
  if (right) return "right";
  if (left) return "left";
  return null;
}

// Markdown table block → styled HTML table (Phase 2 WS-1 F2). Inline styles only —
// the app's single <style> block lives in the Setup screen and unmounts after login.
// Output contains NO newlines so the later \n→<br> pass can't corrupt it.
// The table sits in an overflow-x:auto wrapper so a wide table scrolls sideways inside
// the chat bubble instead of breaking the bubble's width on a phone.
export function mdTable(block) {
  const lines = block.trim().split("\n").map(l=>l.trim()).filter(Boolean);
  if (lines.length < 2) return block;
  // Split on unescaped pipes only; "\|" is a literal pipe inside a cell.
  const cells = l => l.replace(/\\\|/g, PIPE_PLACEHOLDER)
    .replace(/^\|/,"").replace(/\|$/,"").split("|")
    .map(c=>c.trim().split(PIPE_PLACEHOLDER).join("|"));
  // Separator row: only dashes/colons/spaces (the model sometimes emits em/en dashes).
  const isSep = l => /^\|[\s:|\-–—]+\|$/.test(l);
  let head = null, bodyStart = 0, align = [];
  if (isSep(lines[1])) { head = cells(lines[0]); align = cells(lines[1]).map(cellAlign); bodyStart = 2; }
  const rows = lines.slice(bodyStart).filter(l=>!isSep(l)).map(cells);
  if (rows.length === 0 && !head) return block;
  const thStyle = "background:#00427B;color:#fff;padding:6px 10px;border:1px solid #B5D4F4;text-align:left;font-size:12.5px;font-weight:600";
  const tdStyle = "padding:5px 10px;border:1px solid #D7DDE3;font-size:13px;vertical-align:top";
  const alignCss = j => align[j] ? `;text-align:${align[j]}` : "";
  const thead = head ? `<thead><tr>${head.map((h,j)=>`<th style="${thStyle}${alignCss(j)}">${h}</th>`).join("")}</tr></thead>` : "";
  const tbody = rows.map((r,i)=>`<tr>${r.map((c,j)=>`<td style="${tdStyle};background:${i%2?"#F4F6F8":"#FFFFFF"}${alignCss(j)}">${c}</td>`).join("")}</tr>`).join("");
  return `<div style="overflow-x:auto;max-width:100%;margin:8px 0"><table style="border-collapse:collapse;width:100%">${thead}<tbody>${tbody}</tbody></table></div>`;
}

// One list line: optional indent, a marker (- * • or "1." ), whitespace, then the item text.
const LIST_LINE = /^([ \t]*)([-*•]|\d+\.)[ \t]+(.+)$/;

// Indent width in columns; a tab counts as 4 spaces.
function indentWidth(ws) {
  return ws.replace(/\t/g, "    ").length;
}

// A run of consecutive list lines → nested <ul>/<ol> HTML (no newlines in the output).
// A line indented deeper than the item above it opens a list nested INSIDE that item;
// a shallower line closes nested lists back to the matching level. At the same level a
// switch between bullets and numbers closes one list and opens the other, as before.
export function mdList(block) {
  const stack = []; // open lists, innermost last: {indent, tag}
  let html = "";
  block.split("\n").forEach(line => {
    const m = LIST_LINE.exec(line);
    if (!m) return; // the trailing empty string after the last "\n"
    const indent = indentWidth(m[1]);
    const tag = /^\d/.test(m[2]) ? "ol" : "ul";
    const top = stack[stack.length - 1];
    if (!top || indent > top.indent) {
      // First item, or deeper than the current item: open a list (nested in the open <li>).
      html += `<${tag}>`;
      stack.push({indent, tag});
    } else {
      // Close deeper lists until we reach this item's level.
      while (stack.length > 1 && indent < stack[stack.length - 1].indent) {
        html += `</li></${stack.pop().tag}>`;
      }
      const cur = stack[stack.length - 1];
      html += "</li>";
      if (cur.tag !== tag) {
        html += `</${cur.tag}><${tag}>`;
        cur.tag = tag;
      }
    }
    html += `<li>${m[3]}`;
  });
  while (stack.length) html += `</li></${stack.pop().tag}>`;
  return html;
}

export function fmt(text) {
  return text
    .replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/>/g,"&gt;")
    .replace(/```([\s\S]*?)```/g,"<pre>$1</pre>")
    .replace(/`([^`\n]+)`/g,"<code>$1</code>")
    .replace(/\*\*([^*\n]+)\*\*/g,"<b>$1</b>")
    // Tables BEFORE the \n→<br> pass: ≥2 consecutive |…| lines form a table block. Rows may
    // be indented and may carry trailing spaces; a table directly under a text line works.
    .replace(/(?:^[ \t]*\|.*\|[ \t]*$\n?){2,}/gm, block => mdTable(block))
    // Lone dash/em-dash separator lines ("---", "—-") → subtle divider, not raw text.
    .replace(/^[–—-]{2,}[ \t]*$/gm, '<div style="border-top:1px solid rgba(0,0,0,.12);margin:10px 0"></div>')
    .replace(/^###\s+(.+)$/gm,"<h4>$1</h4>")
    .replace(/^##\s+(.+)$/gm,"<h3>$1</h3>")
    .replace(/^#\s+(.+)$/gm,"<h2>$1</h2>")
    // Ordered (1. 2. …) and unordered (- * •) lists → <ol>/<ul>, with indented items nested
    // under their parent item. Each run of consecutive list lines is rendered as one block.
    .replace(/(?:^[ \t]*(?:[-*•]|\d+\.)[ \t]+.+$\n?)+/gm, block => mdList(block))
    .replace(/\n{2,}/g,"</p><p>").replace(/\n/g,"<br>")
    .replace(/^/,"<p>").replace(/$/,"</p>");
}
